import { Button } from '@/components/ui/Button';
import { domainsService } from '@/services/domains.service';
import { useMutation } from '@tanstack/react-query';
import {
  Calendar,
  CheckCircle,
  Download,
  X,
  XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DownloadBulletinsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  folderUuid: string;
  folderName?: string;
}

interface DownloadFormData {
  month: string;
  year: string;
  saveToGed: boolean;
}

const buildLocalizedMonths = (locale: string) => {
  return Array.from({ length: 12 }, (_, idx) => {
    const monthNumber = idx + 1;
    const date = new Date(2000, idx, 1);
    const label = date.toLocaleString(locale || 'fr-FR', { month: 'long' });
    const capitalized = label.charAt(0).toUpperCase() + label.slice(1);
    return { value: String(monthNumber), label: capitalized };
  });
};

const YEARS = Array.from({ length: 3 }, (_, i) => {
  const year = new Date().getFullYear() - i;
  return { value: year.toString(), label: year.toString() };
});

export function DownloadBulletinsDrawer({
  isOpen,
  onClose,
  folderUuid,
  folderName,
}: DownloadBulletinsDrawerProps) {
  const { t, i18n } = useTranslation();
  const MONTHS = useMemo(
    () => buildLocalizedMonths(i18n.language),
    [i18n.language]
  );
  const [formData, setFormData] = useState<DownloadFormData>({
    month: (new Date().getMonth() + 1).toString(),
    year: new Date().getFullYear().toString(),
    saveToGed: false,
  });
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: (params: { month: number; year: number; saveToGed: boolean }) =>
      domainsService.downloadBulletins(folderUuid, params),
    onSuccess: (blob) => {
      // Trigger browser download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const monthStr = formData.month.padStart(2, '0');
      const safeName = (folderName || 'bulletins').replace(/[^a-zA-Z0-9_-]/g, '_');
      link.href = url;
      link.download = `bulletins_${safeName}_${monthStr}_${formData.year}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloadSuccess(true);
    },
  });

  const handleInputChange = (field: keyof DownloadFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    if (!formData.month || !formData.year) return;
    setDownloadSuccess(false);
    mutation.mutate({
      month: parseInt(formData.month),
      year: parseInt(formData.year),
      saveToGed: formData.saveToGed,
    });
  };

  const handleClose = () => {
    setFormData({
      month: (new Date().getMonth() + 1).toString(),
      year: new Date().getFullYear().toString(),
      saveToGed: false,
    });
    setDownloadSuccess(false);
    mutation.reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-xl max-w-xl bg-white shadow-xl transition-transform">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-200 p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50">
                <Download className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">
                  {t('downloadBulletins.title', 'Telecharger les bulletins de paie')}
                </h2>
                <p className="text-sm text-neutral-500">
                  {folderName
                    ? t('downloadBulletins.descriptionWithFolder', 'Telecharger les bulletins pour {{folder}}', { folder: folderName })
                    : t('downloadBulletins.description', 'Selectionner la periode et telecharger le PDF')}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Date Selection */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-neutral-400" />
                  <span className="text-sm font-medium text-neutral-900">
                    {t('downloadBulletins.period', 'Periode')}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">
                      {t('downloadBulletins.month', 'Mois')}
                    </label>
                    <select
                      value={formData.month}
                      onChange={e => handleInputChange('month', e.target.value)}
                      className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                    >
                      {MONTHS.map(month => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">
                      {t('downloadBulletins.year', 'Annee')}
                    </label>
                    <select
                      value={formData.year}
                      onChange={e => handleInputChange('year', e.target.value)}
                      className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                    >
                      {YEARS.map(year => (
                        <option key={year.value} value={year.value}>
                          {year.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Save to GED option */}
              <div className="flex items-center gap-3 rounded-lg border border-neutral-200 p-4">
                <input
                  type="checkbox"
                  id="saveToGed"
                  checked={formData.saveToGed}
                  onChange={e => handleInputChange('saveToGed', e.target.checked)}
                  className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-neutral-300"
                />
                <label htmlFor="saveToGed" className="cursor-pointer">
                  <p className="text-sm font-medium text-neutral-900">
                    {t('downloadBulletins.saveToGed', 'Sauvegarder dans la GED')}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {t('downloadBulletins.saveToGedDescription', 'Les bulletins seront aussi enregistres dans la gestion electronique des documents')}
                  </p>
                </label>
              </div>

              {/* Summary */}
              <div className="rounded-lg bg-neutral-50 p-4">
                <h4 className="mb-2 text-sm font-medium text-neutral-900">
                  {t('downloadBulletins.summary', 'Resume')}
                </h4>
                <div className="space-y-1 text-sm text-neutral-600">
                  <p>
                    <span className="font-medium">{t('downloadBulletins.period', 'Periode')}:</span>{' '}
                    {MONTHS.find(m => m.value === formData.month)?.label}{' '}
                    {formData.year}
                  </p>
                  {formData.saveToGed && (
                    <p>
                      <span className="font-medium">{t('downloadBulletins.gedSave', 'GED')}:</span>{' '}
                      {t('downloadBulletins.willBeSaved', 'Les bulletins seront sauvegardes')}
                    </p>
                  )}
                </div>
              </div>

              {/* Success Message */}
              {downloadSuccess && (
                <div className="rounded-lg bg-emerald-50 p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-900">
                      {t('downloadBulletins.success', 'Bulletins telecharges avec succes')}
                    </span>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {mutation.isError && (
                <div className="rounded-lg bg-red-50 p-4">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-medium text-red-900">
                      {t('downloadBulletins.error', 'Erreur lors du telechargement')}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-red-700">
                    {(mutation.error as unknown as { response?: { data?: { message?: string } } })
                      ?.response?.data?.message ||
                      (mutation.error as unknown as { message?: string })?.message ||
                      t('common.genericError', 'Une erreur est survenue')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-neutral-200 p-6">
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
              >
                {t('common.cancel', 'Annuler')}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!formData.month || !formData.year || mutation.isPending}
                className="flex-1"
              >
                {mutation.isPending
                  ? t('downloadBulletins.downloading', 'Telechargement en cours...')
                  : t('downloadBulletins.download', 'Telecharger')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
